import { ThemeEvents } from '@theme/events';

class ProductSubscription extends HTMLElement {
  /** @type {Record<string, any>} */
  #planMap = {};
  /** @type {string | null} */
  #currentVariantId = null;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#planMap = this.#parsePlanMap();
    this.#currentVariantId = this.dataset.selectedVariantId || this.#getVariantFromForm();

    this.addEventListener('change', this.#handleChange);

    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      this.#abortController = new AbortController();
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
        signal: this.#abortController.signal,
      });
      closestSection.addEventListener('product:switched', this.#handleSectionSwitched, {
        signal: this.#abortController.signal,
      });
    }

    this.#syncFromVariant({ isInitial: true });
  }

  #handleSectionSwitched = () => {
    this.#planMap = this.#parsePlanMap();
    this.#currentVariantId = this.dataset.selectedVariantId || this.#getVariantFromForm();
    this.#syncFromVariant({ isInitial: true });
    this.#syncAddToCartButton();
  };

  #syncAddToCartButton() {
    const scope = this.closest('.shopify-section, dialog') || document;
    const button = scope.querySelector('add-to-cart-component .add-to-cart-text__content');
    if (!(button instanceof HTMLElement)) return;

    const mode = this.dataset.subscriptionMode || this.#getSelectedMode();
    button.textContent = mode === 'subscription'
      ? 'SUBSCRIBE TODAY'
      : Theme.translations.add_to_cart || 'Add to cart';
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.removeEventListener('change', this.#handleChange);
  }

  #parsePlanMap() {
    const mapString = this.dataset.sellingPlanMap;
    if (!mapString) return {};

    try {
      return JSON.parse(mapString);
    } catch (error) {
      console.warn('Product subscription selling plan map could not be parsed.', error);
      return {};
    }
  }

  #getVariantFromForm() {
    const formId = this.dataset.productFormId;
    if (!formId) return null;

    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return null;

    const variantInput = form.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return null;

    return variantInput.value || null;
  }

  #getSellingPlanInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-subscription-selling-plan]'));
  }

  #getSubscriptionSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-subscription-select]'));
  }

  #getModeRadios() {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[data-subscription-mode]'));
  }

  #getSelectedMode() {
    const checked = this.querySelector('input[data-subscription-mode]:checked');
    if (!(checked instanceof HTMLInputElement)) return null;
    return checked.value;
  }

  #getVariantData(variantId) {
    if (!variantId) return null;
    const key = variantId.toString();
    return this.#planMap[key] || null;
  }

  #setMode(mode) {
    const radios = this.#getModeRadios();
    radios.forEach((radio) => {
      radio.checked = radio.value === mode;
    });

    this.dataset.subscriptionMode = mode;

    const select = this.#getSubscriptionSelect();
    const sellingPlanInput = this.#getSellingPlanInput();

    if (mode === 'subscription') {
      if (select) {
        select.disabled = false;
      }
      if (sellingPlanInput) {
        sellingPlanInput.disabled = false;
        if (select?.value) {
          sellingPlanInput.value = select.value;
        }
      }
    } else {
      if (select) {
        select.disabled = true;
      }
      if (sellingPlanInput) {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
      }
    }
  }

  #renderPlanOptions(plans, preferredPlanId = null) {
    const select = this.#getSubscriptionSelect();
    if (!select) return null;

    const currentValue = preferredPlanId || select.value;
    select.innerHTML = '';

    plans.forEach((plan) => {
      const option = document.createElement('option');
      option.value = plan.id?.toString() || '';
      option.textContent = plan.name;
      select.appendChild(option);
    });

    if (currentValue && plans.some((plan) => plan.id?.toString() === currentValue.toString())) {
      select.value = currentValue;
      return currentValue;
    }

    if (plans[0]) {
      select.value = plans[0].id?.toString() || '';
      return plans[0].id?.toString() || null;
    }

    return null;
  }

  #updatePrices(variantData, selectedPlanId) {
    const oneTimePrice = /** @type {HTMLElement | null} */ (this.querySelector('[data-one-time-price]'));
    if (oneTimePrice && variantData?.price) {
      oneTimePrice.textContent = variantData.price;
    }

    const plan =
      variantData?.plans?.find((plan) => plan.id?.toString() === selectedPlanId?.toString()) ||
      variantData?.plans?.[0];
    const subscriptionPrice = /** @type {HTMLElement | null} */ (this.querySelector('[data-subscription-price]'));
    const subscriptionCompare = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-subscription-compare-price]')
    );
    const badge = /** @type {HTMLElement | null} */ (this.querySelector('[data-subscription-badge]'));

    if (subscriptionPrice && plan?.price) {
      subscriptionPrice.textContent = plan.price;
    }

    if (subscriptionCompare) {
      const compareRaw = Number(plan?.compare_at_raw || 0);
      const priceRaw = Number(plan?.price_raw || 0);
      if (compareRaw > priceRaw && plan?.compare_at_price) {
        subscriptionCompare.textContent = plan.compare_at_price;
        subscriptionCompare.removeAttribute('hidden');
      } else {
        subscriptionCompare.textContent = '';
        subscriptionCompare.setAttribute('hidden', 'true');
      }
    }

    if (badge) {
      const compareRaw = Number(plan?.compare_at_raw || 0);
      const priceRaw = Number(plan?.price_raw || 0);
      const adjustmentType = plan?.adjustment_type || null;
      const adjustmentValue = Number(plan?.adjustment_value || 0);
      let discount = 0;

      if (adjustmentType === 'percentage' && adjustmentValue > 0) {
        discount = Math.round(adjustmentValue);
      } else if (adjustmentType === 'fixed_amount' && adjustmentValue > 0) {
        const basePrice = compareRaw || Number(variantData?.price_raw || 0);
        if (basePrice > 0) {
          discount = Math.round((adjustmentValue / basePrice) * 100);
        }
      } else if (compareRaw > priceRaw && compareRaw > 0) {
        discount = Math.round(((compareRaw - priceRaw) / compareRaw) * 100);
      }
      const template = this.dataset.badgeTemplate || '';
      const hasLiquidToken = template.includes('{{ discount }}');
      const hasBraceToken = template.includes('{discount}');
      const token = hasLiquidToken ? '{{ discount }}' : hasBraceToken ? '{discount}' : null;

      if (discount > 0 && token) {
        badge.textContent = template.replace(token, discount.toString());
        badge.removeAttribute('hidden');
      } else if (!token && template.trim() !== '') {
        badge.textContent = template;
        badge.removeAttribute('hidden');
      } else if (discount > 0) {
        badge.textContent = `Save ${discount}%`;
        badge.removeAttribute('hidden');
      } else {
        badge.textContent = '';
        badge.setAttribute('hidden', 'true');
      }
    }

    this.#updatePerOzText(this.dataset.subscriptionMode || this.#getSelectedMode() || 'one-time');
    this.#emitSubscriptionUpdate(variantData, plan, selectedPlanId);
  }

  #syncFromVariant({ isInitial = false } = {}) {
    const variantId = this.#currentVariantId || this.#getVariantFromForm();
    const variantData = this.#getVariantData(variantId);
    const hasPlans = Boolean(variantData?.plans?.length);

    if (variantId) {
      this.dataset.selectedVariantId = variantId.toString();
    }

    this.toggleAttribute('data-subscription-unavailable', !hasPlans);

    const subscriptionRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-subscription-mode][value="subscription"]')
    );
    if (subscriptionRadio) {
      subscriptionRadio.disabled = !hasPlans;
    }

    const select = this.#getSubscriptionSelect();
    const currentMode = this.#getSelectedMode();
    const defaultMode = this.dataset.defaultMode || 'one-time';
    const preferredMode = currentMode || (isInitial ? defaultMode : null) || 'one-time';

    const selectedPlanId = this.#renderPlanOptions(variantData?.plans || [], select?.value || null);

    if (!hasPlans) {
      this.#setMode('one-time');
    } else if (preferredMode === 'subscription') {
      this.#setMode('subscription');
    } else {
      this.#setMode('one-time');
    }

    if (selectedPlanId && this.dataset.subscriptionMode === 'subscription') {
      const sellingPlanInput = this.#getSellingPlanInput();
      if (sellingPlanInput) {
        sellingPlanInput.value = selectedPlanId;
      }
    }

    this.#updatePrices(variantData, selectedPlanId);
  }

  #emitSubscriptionUpdate(variantData, plan, selectedPlanId) {
    const detail = {
      productId: this.dataset.productId || null,
      mode: this.dataset.subscriptionMode || this.#getSelectedMode() || 'one-time',
      variantPrice: variantData?.price || null,
      variantPriceRaw: Number(variantData?.price_raw || 0),
      variantCompare: variantData?.compare_at_price || null,
      variantCompareRaw: Number(variantData?.compare_at_raw || 0),
      planId: selectedPlanId || plan?.id?.toString() || null,
      planPrice: plan?.price || null,
      planPriceRaw: Number(plan?.price_raw || 0),
      planCompare: plan?.compare_at_price || null,
      planCompareRaw: Number(plan?.compare_at_raw || 0),
    };

    this.dispatchEvent(new CustomEvent('subscription:update', { bubbles: true, detail }));
  }

  #updatePerOzText(mode) {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const perOzEls = section.querySelectorAll('.price-per-oz');
    if (!perOzEls.length) return;

    perOzEls.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const oneOz = el.dataset.oneOz;
      const subOz = el.dataset.subOz;
      if (mode === 'subscription' && subOz) {
        el.textContent = subOz;
      } else if (oneOz) {
        el.textContent = oneOz;
      }
    });
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target.matches('input[data-subscription-mode]')) {
      const modeInput = /** @type {HTMLInputElement} */ (event.target);
      this.#setMode(modeInput.value);
      this.#updatePrices(this.#getVariantData(this.#currentVariantId), this.#getSubscriptionSelect()?.value || null);
      return;
    }

    if (event.target.matches('select[data-subscription-select]')) {
      const select = /** @type {HTMLSelectElement} */ (event.target);
      if (this.dataset.subscriptionMode === 'subscription') {
        const sellingPlanInput = this.#getSellingPlanInput();
        if (sellingPlanInput) {
          sellingPlanInput.value = select.value;
        }
      }
      this.#updatePrices(this.#getVariantData(this.#currentVariantId), select.value);
    }
  };

  #handleVariantUpdate = (event) => {
    const detail = event.detail;
    if (!detail?.data) return;

    if (detail.data.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (detail.data.productId && detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newVariantId = detail.resource?.id ? detail.resource.id.toString() : null;
    this.#currentVariantId = newVariantId || this.#getVariantFromForm();

    if (detail.data.newProduct && detail.data.html) {
      const selector = `product-subscription[data-block-id="${this.dataset.blockId}"]`;
      const newBlock = detail.data.html.querySelector(selector);
      if (newBlock instanceof HTMLElement) {
        this.dataset.sellingPlanMap = newBlock.dataset.sellingPlanMap || '';
        this.dataset.selectedVariantId = newBlock.dataset.selectedVariantId || '';
        this.dataset.defaultSellingPlanId = newBlock.dataset.defaultSellingPlanId || '';
        this.dataset.badgeTemplate = newBlock.dataset.badgeTemplate || this.dataset.badgeTemplate || '';
        this.#planMap = this.#parsePlanMap();
      }
    }

    this.#syncFromVariant();
  };
}

if (!customElements.get('product-subscription')) {
  customElements.define('product-subscription', ProductSubscription);
}
